import { useMemo, useState } from "react";
import type { InstanceContact, InstanceContactRequest } from "@keydials/api-client";
import { ApiError } from "@keydials/api-client";
import { mailtoHref, telHref } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";

/** The form's fields, all strings so an input can hold each; blanks are sent as null. */
interface ContactDraft {
  name: string;
  phone: string;
  address: string;
  email: string;
  notes: string;
}

const EMPTY: ContactDraft = { name: "", phone: "", address: "", email: "", notes: "" };

const draftOf = (c: InstanceContact): ContactDraft => ({
  name: c.name,
  phone: c.phone ?? "",
  address: c.address ?? "",
  email: c.email ?? "",
  notes: c.notes ?? "",
});

const requestOf = (d: ContactDraft): InstanceContactRequest => ({
  name: d.name.trim(),
  phone: d.phone.trim() || null,
  address: d.address.trim() || null,
  email: d.email.trim() || null,
  notes: d.notes.trim() || null,
});

/**
 * The dial's Contacts (KEY-752): who the dial DEALS with — the dentist, the GP, the school office — saved once
 * with a number, an address and an email. A "With" field on the dial picks from this list, shows a Call link
 * for the number, and pre-fills "Where" with the address, so nobody types the dentist's details twice.
 *
 * The sibling of Who's who, which is who the dial is ABOUT. Same shape, same bar: only a dial admin writes
 * here, and the server holds the same line.
 */
export function ContactsSection({
  instanceId,
  contacts,
  canManage,
  onChanged,
}: {
  instanceId: string;
  contacts: InstanceContact[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState<ContactDraft | null>(null);
  const [editing, setEditing] = useState<{ id: string; draft: ContactDraft } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(() => [...contacts].sort((a, b) => a.order - b.order), [contacts]);

  async function run(work: (token: string) => Promise<void>) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await work(token);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "That didn’t save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const add = () => {
    if (!adding || !adding.name.trim()) return;
    const request = requestOf(adding);
    void run(async (token) => {
      await api.addInstanceContact(token, instanceId, request);
      setAdding(null);
    });
  };

  const saveEdit = () => {
    if (!editing || !editing.draft.name.trim()) return;
    const { id, draft } = editing;
    void run(async (token) => {
      await api.updateInstanceContact(token, instanceId, id, requestOf(draft));
      setEditing(null);
    });
  };

  const remove = (contact: InstanceContact) => {
    if (!window.confirm(`Take ${contact.name} off the list? Anything already recorded with them is kept.`)) return;
    void run((token) => api.removeInstanceContact(token, instanceId, contact.id));
  };

  const move = (index: number, delta: number) => {
    const next = [...sorted];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    void run((token) => api.reorderInstanceContacts(token, instanceId, next.map((c) => c.id)));
  };

  return (
    <SettingsCard
      title="Contacts"
      subtitle="Dentists, doctors, schools — anyone this dial deals with. Save their number and address once; fields on the dial that ask “with?” pick from this list, offer a Call link, and fill in where to go."
    >
      {sorted.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>No contacts saved yet.</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
          {sorted.map((c, i) => (
            <li key={c.id} style={{ padding: "10px 0", borderTop: i === 0 ? "none" : "var(--border-w) solid var(--border)" }}>
              {editing?.id === c.id ? (
                <ContactForm
                  draft={editing.draft}
                  busy={busy}
                  submitLabel="Save"
                  onChange={(draft) => setEditing({ id: c.id, draft })}
                  onSubmit={saveEdit}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <ContactSummary contact={c} />
                  {canManage && (
                    <span style={{ display: "inline-flex", gap: 6 }}>
                      <button type="button" className="kd-btn kd-btn-sm" aria-label={`Move ${c.name} up`} disabled={busy || i === 0} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className="kd-btn kd-btn-sm" aria-label={`Move ${c.name} down`} disabled={busy || i === sorted.length - 1} onClick={() => move(i, 1)}>↓</button>
                      <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => setEditing({ id: c.id, draft: draftOf(c) })}>Edit</button>
                      <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => remove(c)}>Remove</button>
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canManage && (
        adding ? (
          <div style={{ marginTop: 12 }}>
            <ContactForm
              draft={adding}
              busy={busy}
              submitLabel="Add"
              onChange={setAdding}
              onSubmit={add}
              onCancel={() => setAdding(null)}
            />
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <button type="button" className="kd-btn-primary" onClick={() => setAdding(EMPTY)}>Add a contact</button>
          </div>
        )
      )}
      {error && <p role="alert" style={{ margin: "10px 0 0", fontSize: 13, color: "var(--st-red, #b91c1c)" }}>{error}</p>}
    </SettingsCard>
  );
}

/** One contact as the list shows it: name, then whatever details it has, the number and email as links. */
function ContactSummary({ contact }: { contact: InstanceContact }) {
  const call = telHref(contact.phone);
  const mail = mailtoHref(contact.email);
  const detail = { fontSize: 12.5, color: "var(--text-muted)" } as const;
  return (
    <div style={{ flex: "1 1 220px", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 14, color: "var(--text)" }}>{contact.name}</span>
      {contact.phone && (call ? <a href={call} style={{ ...detail, color: "var(--accent-text)" }}>{contact.phone}</a> : <span style={detail}>{contact.phone}</span>)}
      {contact.email && mail && <a href={mail} style={{ ...detail, color: "var(--accent-text)", overflowWrap: "anywhere" }}>{contact.email}</a>}
      {contact.address && <span style={{ ...detail, whiteSpace: "pre-line" }}>{contact.address}</span>}
      {contact.notes && <span style={{ ...detail, whiteSpace: "pre-line", fontStyle: "italic" }}>{contact.notes}</span>}
    </div>
  );
}

/** The add / edit form. Name is the only thing required; the server checks a phone or email looks like one. */
function ContactForm({ draft, busy, submitLabel, onChange, onSubmit, onCancel }: {
  draft: ContactDraft;
  busy: boolean;
  submitLabel: string;
  onChange: (draft: ContactDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<ContactDraft>) => onChange({ ...draft, ...patch });
  const row = { display: "flex", gap: 8, flexWrap: "wrap" } as const;
  return (
    <form
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      <div style={row}>
        <input className="field" aria-label="Name" placeholder="Name — e.g. Smile Dental" value={draft.name} maxLength={120}
          onChange={(e) => set({ name: e.target.value })} style={{ flex: "1 1 200px", minWidth: 0 }} />
        <input className="field" aria-label="Phone" placeholder="Phone" type="tel" inputMode="tel" autoComplete="off" value={draft.phone} maxLength={40}
          onChange={(e) => set({ phone: e.target.value })} style={{ flex: "1 1 160px", minWidth: 0 }} />
        <input className="field" aria-label="Email" placeholder="Email" type="email" autoComplete="off" value={draft.email} maxLength={254}
          onChange={(e) => set({ email: e.target.value })} style={{ flex: "1 1 200px", minWidth: 0 }} />
      </div>
      <textarea className="field" aria-label="Address" placeholder="Address" rows={3} value={draft.address} maxLength={500}
        onChange={(e) => set({ address: e.target.value })} style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }} />
      <textarea className="field" aria-label="Notes" placeholder="Notes — opening hours, who to ask for, a reference number" rows={2} value={draft.notes} maxLength={1000}
        onChange={(e) => set({ notes: e.target.value })} style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="kd-btn-primary kd-btn-sm" disabled={busy || !draft.name.trim()}>{submitLabel}</button>
        <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
